
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ToastrService } from 'ngx-toastr';
import { MatTooltipModule } from '@angular/material/tooltip';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 5; // Maximum number of files allowed

@Component({
  selector: 'app-add-document-dialog',
  standalone: true,
  imports: [MatDividerModule, MatDialogModule, MatButtonModule, CommonModule, MatTableModule, MatProgressBarModule, MatTooltipModule ],
  templateUrl: './add-document-dialog.component.html',
  styleUrl: './add-document-dialog.component.scss'
})
export class AddDocumentDialogComponent {

  selectedFiles: File[] = [];
  isLoading = false; // For file selection
  uploading = false; // For upload overlay
  // uploadProgress = 0; // Track upload progress
  displayedColumns: string[] = ['serialNumber', 'fileName', 'fileSize', 'actions'];
  isMaxSizeExceeded = false; // For file size validation
  isValidFileType = false; // For file type validation

  constructor(public dialogRef: MatDialogRef<AddDocumentDialogComponent>,
    private readonly toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {

  }

  onFileSelected(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const files = inputElement.files;

    if (files && files.length > 0) {
      this.isLoading = true;

      setTimeout(() => {
        const newFiles = Array.from(files).filter(
          (file) => !this.selectedFiles.some((f) => f.name === file.name && f.size === file.size)
        );
        this.selectedFiles = [...this.selectedFiles, ...newFiles];
        this.isLoading = false;
      }, 1500);
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;

    if (files && files.length > 0) {
      this.isLoading = true;

      setTimeout(() => {
        const newFiles = Array.from(files).filter(
          (file) => !this.selectedFiles.some((f) => f.name === file.name && f.size === file.size)
        );
        this.selectedFiles = [...this.selectedFiles, ...newFiles];
        this.isLoading = false;
      }, 1500);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  removeFile(index: number) {
    this.selectedFiles = this.selectedFiles.slice(0, index).concat(this.selectedFiles.slice(index + 1));
  }

  async uploadDocuments() {
    this.uploading = true;

    if (this.selectedFiles?.length === 0) {
      this.toastr.warning('Please select files to upload.');
      this.uploading = false;
      return;
    }
    this.isMaxSizeExceeded = this.selectedFiles.some(file => file.size > MAX_FILE_SIZE);
    this.isValidFileType = this.selectedFiles.every(file => {
      return file.name.toLowerCase().endsWith('.docx')
      || file.name.toLowerCase().endsWith('.doc')
      || file.name.toLowerCase().endsWith('.rtf')
      || file.name.toLowerCase().endsWith('.txt')
      || file.name.toLowerCase().endsWith('.xls')
      || file.name.toLowerCase().endsWith('.xlsx')
      || file.name.toLowerCase().endsWith('.xlsm')
      || file.name.toLowerCase().endsWith('.pdf')
      || file.name.toLowerCase().endsWith('.jpg')
      || file.name.toLowerCase().endsWith('.jpeg')
      || file.name.toLowerCase().endsWith('.png');
    });
    
    if(this.isMaxSizeExceeded) {
      this.toastr.error('File size exceeds 10MB,please try with smaller file size.');
      this.uploading = false;
      return;
    }
    
    if(this.selectedFiles?.length > MAX_FILES) {
      this.toastr.error('You can only upload a maximum of 5 files.Please remove extra files & try again.');
      this.uploading = false;
      return;
    }
    if(!this.isValidFileType) {
      this.toastr.error('Invalid file type. Please upload only .docx, .doc, .rtf, .txt, .xls, .xlsx, .xlsm, .pdf, .jpg, .jpeg or .png files.');
      this.uploading = false;
      return;
    }
    if(this.selectedFiles?.length <= MAX_FILES && !this.isMaxSizeExceeded && this.isValidFileType) {
      const formData = new FormData();
      this.selectedFiles?.forEach(file => formData.append('files', file, file.name));
      this.dialogRef.close(this.selectedFiles);
    }
  }
  getFileSize(size: number): string {
    const mb = size / (1024 * 1024);
    return `${mb.toFixed(3)}MB`;
  }
  closeDialog() {
    this.dialogRef.close();
  }

  getFileTypeLabel(fileName: string): string {
  const name = fileName.toLowerCase();
  if (name.endsWith('.pdf')) return 'PDF file';
  if (name.endsWith('.doc') || name.endsWith('.docx')) return 'Word file';
  if (name.endsWith('.rtf')) return 'Rich Text file';
  if (name.endsWith('.txt')) return 'Text file';
  if (name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.xlsm')) return 'Excel file';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'JPG file';
  if (name.endsWith('.png')) return 'PNG file';
  return 'File';
}

}
